import { Component, inject, OnInit } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { DeleteCloudProjectOdxTableComponent } from "@components/cloud-project-odx-tables/delete-cloud-project-odx-table/delete-cloud-project-odx-table.component";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { ProjectApiService } from "@domain/project/api/project-api.service";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService } from "@odx/angular/components/modal";
import { ToastHelper } from "@utils/toast-helper.service";
import { map, Observable, Subject, switchMap, tap } from "rxjs";

@Component({
  templateUrl: "./cloud-project-delete-modal.component.html",
  styleUrls: ["./cloud-project-delete-modal.component.scss"],
  imports: [UiKitModule, DeleteCloudProjectOdxTableComponent],
})
export class CloudProjectDeleteModalComponent implements OnInit {
  private projectService = inject(ProjectService);
  private modalService = inject(ModalService);
  private toastHelper = inject(ToastHelper);
  private apiService = inject(ProjectApiService);
  private userService = inject(UserService);

  protected cloudProjects$ = new Subject<CloudProjectMetadata[]>();
  protected userId!: string;

  ngOnInit() {
    this.userService.user$
      .pipe(
        map((user) => user.id),
        tap((userId) => (this.userId = userId)),
        switchMap((userId) => this.loadCloudProjects(userId)),
      )
      .subscribe();
  }

  onClickDelete(cloudProject: CloudProjectMetadata) {
    this.projectService.findLocalProjectByCloudId(cloudProject.id).subscribe((project) => {
      if (project) {
        this.toastHelper.error(
          $localize`:@@modal.deleteCloudProjects.error:Das Projekt ${cloudProject.displayName}:projectName: muss zuerst aus "Meine Projekte" entfernt werden, um es aus der Cloud zu löschen.`,
        );
        return;
      }
      this.modalService
        .open(ConfirmModalComponent, {
          data: {
            text: $localize`:@@modal.deleteCloudProjects.askForConfirmation:Möchten Sie dieses Projekt wirklich aus der Cloud löschen? Eine Wiederherstellung ist nicht mehr möglich.`,
          },
        })
        .onClose$.pipe(
          switchMap(() => this.apiService.deleteProjectFromCloud(cloudProject.id)),
          switchMap(() => this.loadCloudProjects(this.userId)),
        )
        .subscribe(() => {
          this.toastHelper.success(
            $localize`:@@toast.deleteCloudProjectSuccessful:Das Projekt ${cloudProject.displayName}:projectName: wurde erfolgreich aus der Cloud gelöscht.`,
          );
        });
    });
  }

  private loadCloudProjects(userId: string): Observable<void> {
    return this.projectService.loadCloudProjectsMetadata().pipe(
      map((cloudProjects) => {
        const filteredProjects = cloudProjects.filter((project) => {
          const owner = project.users[0] as any;
          return userId === owner.id;
        });
        this.cloudProjects$.next(filteredProjects);
      }),
    );
  }
}
